import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { parseTasks } from '../general_components/content_management/task_list/taskListElements/diagramView';
import { lockedCode } from './truckStatus';
import { statusCodeGenerator } from './teamCard/truckCard/truckCardComponents/statusSection';
import { parseAndSortNotes } from './teamCard/truckCard/truckCardComponents/notesSection';
import { DateTime } from 'luxon';
import { API_ROOT, sendRequest } from '../supplementary_components/requests';
import { useDriversStore } from './teamCard/truckCard/hos-context';
import { createTimeAnchor, liveDateTimeFromAnchor, liveIsoFromAnchor } from './serverClock';
import { retrieveMainBoardCustomizations } from '../supplementary_components/userDataDelegate';
import { retrieveCompanyData } from '../supplementary_components/companySettings';
import {
	DEFAULT_BOARD_STATISTICS,
	calculateJoinedPayoutGross,
	calculateTruckPayoutRpm,
	calculateTruckWeeklyRpm,
	followsWeeklyOwnerPayoutFromCompany,
	loadHasBoardShare,
} from './plannerBoardSettings';
import { calculateTruckDriverPayoutTotal, sanitizeThisWeeksDriverPayoutLoadsAmounts } from '../supplementary_components/driverPayoutUtils';

// Normalized store for MainBoard:
// - trucksById: truck-level fields + relationships (load IDs) + derived fields used for sorting/rendering
// - loadsById: load entities keyed by loadId
// - teams: team list with truckIds (render structure)
//
// Goal: load updates only mutate loadsById -> only subscribers that select loads rerender.

function ensureArray(val) {
if (!val) return [];
return Array.isArray(val) ? val : [val];
}

function normalizeBoardNotes(input) {
if (!Array.isArray(input)) return [];
// Backwards compatibility: some APIs may deliver notes as JSON strings.
if (input.length > 0 && typeof input[0] === 'string') {
	const parsed = [];
	for (const s of input) {
	if (typeof s !== 'string') continue;
	try {
		const obj = JSON.parse(s);
		if (obj && typeof obj === 'object') parsed.push(obj);
	} catch (e) {
		// ignore malformed entries
	}
	}
	return parsed;
}
return input.filter((x) => x && typeof x === 'object');
}

function boardNoteTimeMillis(note) {
if (!note || typeof note !== 'object') return 0;
// Prefer a real timestamp if backend provides one; otherwise parse display string.
const raw = note.lastChangedTime || note.dateTime || null;
if (!raw) return 0;

// If it's ISO, Luxon will parse; if it's formatted like "07:26 12/18/2025", parse that.
let dt = DateTime.fromISO(raw, { setZone: true });
if (!dt.isValid) {
	dt = DateTime.fromFormat(String(raw), 'HH:mm MM/dd/yyyy');
}
return dt.isValid ? dt.toMillis() : 0;
}

function sortBoardNotes(notes) {
const arr = Array.isArray(notes) ? [...notes] : [];
// Requirement:
// - urgent notes first
// - latest added (non-urgent) comes last (i.e. non-urgent in ascending time order)
//
// For urgent notes (not specified), show newest first so the most relevant urgent items are visible.
arr.sort((a, b) => {
	const au = Boolean(a?.urgent);
	const bu = Boolean(b?.urgent);
	if (au !== bu) return au ? -1 : 1;

	const at = boardNoteTimeMillis(a);
	const bt = boardNoteTimeMillis(b);

	if (au) return bt - at; // urgent: newest first
	return at - bt; // non-urgent: oldest first, newest last
});
return arr;
}

/** Set when persisting board current load or {@code GET /loads/:id} — blocks planner DTO overwrites. */
function markFullLoad(load) {
	return load ? { ...load, full: true } : load;
}

function isIncrementalLoadPatch(load) {
	const keys = Object.keys(load || {}).filter((k) => k !== 'id' && k !== 'full');
	return keys.length > 0 && keys.length <= 5;
}

function loadStatusChanged(existing, load) {
	if (!existing || !load || load.loadStatus == null) return false;
	return String(load.loadStatus) !== String(existing.loadStatus);
}

/** Full loads are protected from planner DTOs except small patches and status transitions. */
function shouldMergeIntoFullLoad(existing, load) {
	return isIncrementalLoadPatch(load) || loadStatusChanged(existing, load);
}

function upsertLoadMap(loadsById, load) {
	if (!load || !load.id) return loadsById;
	const id = load.id;
	const existing = loadsById[id];
	if (!existing) {
		return { ...loadsById, [id]: { ...load } };
	}
	if (existing.full === true && load.full !== true) {
		if (shouldMergeIntoFullLoad(existing, load)) {
			return { ...loadsById, [id]: { ...existing, ...load, full: true } };
		}
		return loadsById;
	}
	const merged = { ...existing, ...load };
	if (load.full === true || existing.full === true) {
		merged.full = true;
	}
	return { ...loadsById, [id]: merged };
}

function deriveTruckFromRaw(rawTruck, currentTime) {
const currentLoad = rawTruck.currentLoad || null;
const currentLoadId = currentLoad?.id ?? null;
const currentLoadNumber = currentLoad?.loadNumber ?? null;
const currentLoadStops = currentLoad?.stops ?? null;

const parsedTasksObject = parseTasks(
	rawTruck.tasks,
	true,
	currentLoadNumber,
	currentLoadId,
	currentLoadStops,
	currentTime,
	currentLoad
);

const derived = {
	tasks: parsedTasksObject.parsedTasks,
	activeLoadIds: parsedTasksObject.activeLoadIds,
	lastLoadsLastTimecode: parsedTasksObject.lastLoadsLastTimecode || 0,
	loadStopsAndTasksObject: parsedTasksObject.loadStopsAndTasksObject,
	truckIsAtHome: parsedTasksObject.truckIsAtHome === true,
	truckIsOpen: !parsedTasksObject.truckIsAtHome && parsedTasksObject.truckIsOpen,
};

return {
	...rawTruck,
	...derived,
	status: statusCodeGenerator(derived.tasks),
	allNotes: parseAndSortNotes({ ...rawTruck, ...derived }),
	currentLoadId,
};
}

export const useBoardStore = create(
subscribeWithSelector((set, get) => ({
	// snapshot-ish state
	currentTime: null,
	/** Server time seed + performance.now() anchor; used for live "now" without machine wall clock. */
	timeAnchor: null,
	plannerTick: 0,
	/** Bumped once an hour so Truck Status can lock when an appointment crosses its window. */
	statusClock: 0,
	teams: null, // [{ teamName, truckIds: [] , ...optional}]

	// entities
	trucksById: {}, // { [truckId]: TruckEntity }
	loadsById: {}, // { [loadId]: LoadEntity }

	// other board blocks
	unassignedLoads: [],
	boardNotes: [],
	
	// Track whether initial location data has been loaded (so we can show '-' instead of loading icon)
	initialLocationsLoaded: false,

	openTrucks: new Set(), // Set of truckIds that are open
	trucksAtHome: new Set(), // Set of truckIds that are at home
	trucksTotalGross: {}, // { [truckId]: number }
	trucksTotalDriverPayout: {}, // { [truckId]: number }
	trucksWeeklyRpm: {}, // { [truckId]: number | null }
	trucksPayoutGross: {}, // { [truckId]: number }
	trucksPayoutRpm: {}, // { [truckId]: number | null }
	/** null = unrestricted; Set of string truckIds when ratesRestrictedPerTeam applies for viewer */
	grossVisibleTruckIds: null,

	/** Planner Mode UI settings (from mainBoardCustomizations; not cleared on board refresh). */
	horizontalScrollTimeline: true,
	boardStatistics: { ...DEFAULT_BOARD_STATISTICS },
	plannerColumns: null,
	/** Raw saved pref; MB_GROSS applies updater/rates gates via resolveShowDriverPayoutGross. */
	showDriverPayoutGross: false,
	plannerUiSettingsVersion: 0,

	hydratePlannerUiSettings: () => {
		const c = retrieveMainBoardCustomizations();
		set({
			horizontalScrollTimeline: c.horizontalScrollTimeline !== false,
			boardStatistics: c.boardStatistics
				? { ...DEFAULT_BOARD_STATISTICS, ...c.boardStatistics }
				: { ...DEFAULT_BOARD_STATISTICS },
			plannerColumns: Array.isArray(c.plannerColumns) ? c.plannerColumns : null,
			showDriverPayoutGross: c.showDriverPayoutGross === true,
			plannerUiSettingsVersion: (get().plannerUiSettingsVersion || 0) + 1,
		});
	},

	// Invoices Page State
	invoiceIds: [], // array of load IDs for the invoice page
	/** From loads/invoicesPage: notFactoredInvoicedLoads */
	notFactoredManagableLoadExists: false,
	/** From loads/invoicesPage: notFactoredCompletedLoadCount */
	completedButNotFactoredLoadsCount: 0,

	filterText: "",

    setFilterText: (text) => {
		const { filterText } = get();
		if (filterText === text) return;

		set({ filterText: text });
		get().recomputeVisibleTrucks();
	  },
	visibleTruckIds: new Set(),

    recomputeVisibleTrucks: () => {
		const { filterText, trucksById, loadsById } = get();
	  
		// Normalize tokens
		const tokens = filterText
		  .split(';')
		  .map(t => t.trim())
		  .filter(Boolean);
		// No filter → everything visible
		if (tokens.length === 0) {
		  set({ visibleTruckIds: new Set(Object.keys(trucksById)) });
		  return;
		}
	  
		const result = new Set();
	  
		for (const truckId of Object.keys(trucksById)) {
			const truck = trucksById[truckId];
			if (!truck) continue;
		
			const currentLoad = truck.currentLoadId
			? loadsById[truck.currentLoadId]
			: null;
			// Get upcoming loads
			const upcomingLoads = (truck.upcomingLoadIdsForPlannerView || [])
				.map(loadId => loadsById[loadId])
				.filter(Boolean)
				.map(load => ({
					loadNumber: load.loadNumber,
					broker: load.broker?.brokerageName
				}));
			const searchable = {
			driver1: truck.driver1,
			driver2: truck.driver2,
			currentTrailerNumber: truck.currentTrailer?.trailerNumber,
			currentLoad: currentLoad
				? {
					loadNumber: currentLoad.loadNumber,
					broker: currentLoad.broker?.brokerageName
				}
				: null,
				upcomingLoads: upcomingLoads
			};
		
			// ALL tokens must match
			const matchesAll = tokens.some(token =>
				matchesTruckData(truck.truckNumber, searchable, token)
			);
		
			if (matchesAll) {
			result.add(truckId);
			}
		}
	  
		set({ visibleTruckIds: result });
	},

	// Store a stable reference so PopupWindow can always derive the latest entity from state.
	// { type: 'load', id } | { type: 'truck', truckId } | { type: 'raw', obj }
	popupSelection: null,
	openPopupLoad: (id) => set({ popupSelection: id ? { type: 'load', id: Number(id) } : null }),
	openPopupTruck: (truckId) => set({ popupSelection: truckId != null ? { type: 'truck', truckId: String(truckId) } : null }),
	setPopupRaw: (obj) => set({ popupSelection: obj ? { type: 'raw', obj } : null }),
	closePopup: () => set({ popupSelection: null }),

	clearBoard: () =>
	set({
		currentTime: null,
		timeAnchor: null,
		plannerTick: 0,
		teams: null,
		trucksById: {},
		loadsById: {},
		unassignedLoads: [],
		boardNotes: [],
		initialLocationsLoaded: false,
		invoiceIds: [],
		notFactoredManagableLoadExists: false,
		completedButNotFactoredLoadsCount: 0,
		popupSelection: null,
		filterText: null,
		trucksAtHome: new Set(),
		openTrucks: new Set(),
		trucksTotalGross: {},
		trucksTotalDriverPayout: {},
		trucksWeeklyRpm: {},
		trucksPayoutGross: {},
		trucksPayoutRpm: {},
		grossVisibleTruckIds: null,
		visibleTruckIds: new Set(),
	}),
	bumpPlannerTick: () => set((s) => ({ ...s, plannerTick: (s.plannerTick || 0) + 1 })),
	bumpStatusClock: () => set((s) => ({ ...s, statusClock: (s.statusClock || 0) + 1 })),

	/** Live "now" derived from last server currentTime + monotonic elapsed (not DateTime.now()). */
	getLiveCurrentTime: () => liveDateTimeFromAnchor(get().timeAnchor),
	getLiveCurrentTimeIso: () => liveIsoFromAnchor(get().timeAnchor) ?? get().currentTime ?? null,
	
	setInitialLocationsLoaded: (loaded) => set({ initialLocationsLoaded: loaded }),

	hydrateFromSnapshot: (snapshot, plannerMode) => {
	if (!snapshot) return;
	let nextLoadsById = {};
	const nextTrucksById = {};
	const nextTeams = [];
	const nextTrucksAtHome = new Set();
	const nextOpenTrucks = new Set();
	const nextTrucksTotalGross = {};
	const nextTrucksTotalDriverPayout = {};
	const nextTrucksWeeklyRpm = {};
	const nextTrucksPayoutGross = {};
	const nextTrucksPayoutRpm = {};
	const currentTime = snapshot.currentTime || null;
	const timeAnchor = createTimeAnchor(currentTime);
	const rawGrossVisible = snapshot.grossVisibleTruckIds;
	const nextGrossVisibleTruckIds = Array.isArray(rawGrossVisible)
		? new Set(rawGrossVisible.map((id) => String(id)))
		: null;

	const rawTeams = snapshot.teams || [];
	for (const team of rawTeams) {
		const truckIds = [];
		const rawTrucks = team.trucks || [];
		
		for (const rawTruck of rawTrucks) {
		// Planner loads first; full currentLoad last so it is not thinned by planner DTOs.
		for (const l of ensureArray(rawTruck.previousLoadsForPlannerView)) {
			nextLoadsById = upsertLoadMap(nextLoadsById, l);
		}
		for (const l of ensureArray(rawTruck.upcomingLoadsForPlannerView)) {
			nextLoadsById = upsertLoadMap(nextLoadsById, l);
		}
		if (rawTruck.currentLoad) {
			nextLoadsById = upsertLoadMap(nextLoadsById, markFullLoad(rawTruck.currentLoad));
		}

		const derivedTruck = deriveTruckFromRaw(rawTruck, currentTime);
		const truckId = derivedTruck.id != null ? String(derivedTruck.id) : null;
		if (truckId) {
			truckIds.push(truckId);
			// Store relationships as IDs (so loads can update without touching truck)
			if (derivedTruck.truckIsAtHome) {
				nextTrucksAtHome.add(truckId);
			}
			if (derivedTruck.truckIsOpen) {
				nextOpenTrucks.add(truckId);
			}
			if (
				nextGrossVisibleTruckIds != null &&
				!nextGrossVisibleTruckIds.has(truckId)
			) {
				if (derivedTruck.thisWeeksLoads) {
					derivedTruck.thisWeeksLoads = sanitizeThisWeeksLoadsAmounts(derivedTruck.thisWeeksLoads);
				}
				if (derivedTruck.thisWeeksDriverPayoutLoads) {
					derivedTruck.thisWeeksDriverPayoutLoads = sanitizeThisWeeksDriverPayoutLoadsAmounts(
						derivedTruck.thisWeeksDriverPayoutLoads
					);
				}
			}
			if (derivedTruck.thisWeeksLoads || derivedTruck.thisWeeksDriverPayoutLoads) {
				assignTruckRateTotals(
					truckId,
					derivedTruck,
					{
						trucksTotalGross: nextTrucksTotalGross,
						trucksTotalDriverPayout: nextTrucksTotalDriverPayout,
						trucksWeeklyRpm: nextTrucksWeeklyRpm,
						trucksPayoutGross: nextTrucksPayoutGross,
						trucksPayoutRpm: nextTrucksPayoutRpm,
					}
				);
			}
			nextTrucksById[truckId] = {
			...derivedTruck,
			// strip nested load objects from truck entity to prevent coupling
			currentLoad: undefined,
			previousLoadsForPlannerView: undefined,
			upcomingLoadsForPlannerView: undefined,
			previousLoadIdsForPlannerView: ensureArray(rawTruck.previousLoadsForPlannerView).map((x) => x.id),
			upcomingLoadIdsForPlannerView: ensureArray(rawTruck.upcomingLoadsForPlannerView).map((x) => x.id),
			truckIsAtHome: undefined,	
			truckIsOpen: undefined,
		};
		}
		}
		nextTeams.push({
		...team,
		trucks: undefined,
		truckIds,
		});
	}

	// Apply trucksInitialLocation from snapshot (lastSavedUpdate); if present, mark initial locations as loaded.
	// Backend keys this map by String(truckId) for trucks and external trackingDriverId for drivers.
	// Driver entries carry hosStatus; truck entries don't.
	const lastSavedUpdate = snapshot.lastSavedUpdate;
	if (lastSavedUpdate && typeof lastSavedUpdate === 'object') {
		for (const key of Object.keys(lastSavedUpdate)) {
			const patch = lastSavedUpdate[key];
			if (!patch || typeof patch !== 'object') continue;
			if ('hosStatus' in patch) {
				useDriversStore.getState().upsertDriver(key, patch);
				continue;
			}
			const truck = nextTrucksById[key];
			if (truck) {
				nextTrucksById[key] = { ...truck, ...patch };
			}
		}
	}

	set({
		currentTime,
		timeAnchor,
		teams: nextTeams,
		trucksById: nextTrucksById,
		loadsById: nextLoadsById,
		unassignedLoads: snapshot.unassignedLoads || [],
		boardNotes: snapshot.boardNotes ? sortBoardNotes(normalizeBoardNotes(snapshot.boardNotes)) : [],
		trucksAtHome: nextTrucksAtHome,
		openTrucks: nextOpenTrucks,
		trucksTotalGross: nextTrucksTotalGross,
		trucksTotalDriverPayout: nextTrucksTotalDriverPayout,
		trucksWeeklyRpm: nextTrucksWeeklyRpm,
		trucksPayoutGross: nextTrucksPayoutGross,
		trucksPayoutRpm: nextTrucksPayoutRpm,
		grossVisibleTruckIds: nextGrossVisibleTruckIds,
		initialLocationsLoaded: !!lastSavedUpdate,
	});
	},

	// patch truck fields from websocket updates
	patchTruck: (truckId, patch, currentTimeOverride) => {
	if (truckId == null || !patch) return;
	truckId = String(truckId);
	const currentTime = currentTimeOverride ?? get().getLiveCurrentTimeIso() ?? get().currentTime ?? null;
	const liveNow = get().getLiveCurrentTime?.();
	const statusNowMs = liveNow && typeof liveNow.toMillis === 'function' ? liveNow.toMillis() : Date.now();
	set((s) => {
		
		const prevTruck = s.trucksById[truckId];
		if (!prevTruck) return s;

		// handle tasks updates (derive fields)
		let nextTruck = { ...prevTruck };
		let tasksWereEdited = false;
		let truckIsAtHome = null; 
		let truckIsOpen = null;
		if ('tasks' in patch) {
			
			tasksWereEdited = true;
			const parsed = parseTasks(
				patch.tasks,
				true,
				prevTruck.currentLoadId ? (s.loadsById[prevTruck.currentLoadId]?.loadNumber ?? prevTruck.currentLoadNumber) : prevTruck.currentLoadNumber,
				prevTruck.currentLoadId,
				prevTruck.currentLoadId ? s.loadsById[prevTruck.currentLoadId]?.stops : null,
				currentTime,
				prevTruck.currentLoad
			);
			nextTruck.tasks = parsed.parsedTasks;
			nextTruck.activeLoadIds = parsed.activeLoadIds;
			nextTruck.lastLoadsLastTimecode = parsed.lastLoadsLastTimecode || 0;
			nextTruck.loadStopsAndTasksObject = parsed.loadStopsAndTasksObject;
			nextTruck.status = statusCodeGenerator(nextTruck.tasks);
			// Get truckIsAtHome from parsed result
			
			truckIsAtHome = parsed.truckIsAtHome === true;
			truckIsOpen = !parsed.truckIsAtHome && parsed.truckIsOpen;
			// In planner view, tasks define the current/upcoming active load IDs ordering.
			if (parsed.activeLoadIds && parsed.activeLoadIds.length > 0) {
				nextTruck.currentLoadId = parsed.activeLoadIds[0] || null;
				nextTruck.upcomingLoadIdsForPlannerView = parsed.activeLoadIds.slice(1);
				
				// Remove any loads that are now current/upcoming from the completed list
				const activeIds = new Set(parsed.activeLoadIds.map(id => Number(id)));
				const prevCompleted = Array.isArray(prevTruck.previousLoadIdsForPlannerView) ? prevTruck.previousLoadIdsForPlannerView : [];
				nextTruck.previousLoadIdsForPlannerView = prevCompleted.filter(id => !activeIds.has(Number(id)));
			} else {
				nextTruck.currentLoadId = null;
				nextTruck.upcomingLoadIdsForPlannerView = [];
			}
		}

		// merge other truck-level fields (but never accept nested loads into truck entity)
		const forbidden = new Set([
			'currentLoad',
			'upcomingLoad',
			'completedLoadData',
			'previousLoadsForPlannerView',
			'upcomingLoadsForPlannerView',
		]);
		for (const key of Object.keys(patch)) {
			if (key === 'tasks') continue;
			if (forbidden.has(key)) continue;
			nextTruck[key] = patch[key];
		}
		const nextTrucksTotalGross = { ...s.trucksTotalGross };
		const nextTrucksTotalDriverPayout = { ...s.trucksTotalDriverPayout };
		const nextTrucksWeeklyRpm = { ...s.trucksWeeklyRpm };
		const nextTrucksPayoutGross = { ...s.trucksPayoutGross };
		const nextTrucksPayoutRpm = { ...s.trucksPayoutRpm };
		if ('thisWeeksLoads' in patch) {
			if (
				s.grossVisibleTruckIds != null &&
				!s.grossVisibleTruckIds.has(truckId)
			) {
				nextTruck.thisWeeksLoads = sanitizeThisWeeksLoadsAmounts(nextTruck.thisWeeksLoads);
			}
		}
		if ('thisWeeksDriverPayoutLoads' in patch) {
			if (
				s.grossVisibleTruckIds != null &&
				!s.grossVisibleTruckIds.has(truckId)
			) {
				nextTruck.thisWeeksDriverPayoutLoads = sanitizeThisWeeksDriverPayoutLoadsAmounts(
					nextTruck.thisWeeksDriverPayoutLoads
				);
			}
		}
		if ('thisWeeksLoads' in patch || 'thisWeeksDriverPayoutLoads' in patch) {
			assignTruckRateTotals(
				truckId,
				nextTruck,
				{
					trucksTotalGross: nextTrucksTotalGross,
					trucksTotalDriverPayout: nextTrucksTotalDriverPayout,
					trucksWeeklyRpm: nextTrucksWeeklyRpm,
					trucksPayoutGross: nextTrucksPayoutGross,
					trucksPayoutRpm: nextTrucksPayoutRpm,
				}
			);
		}
		// If notes-related fields change, re-derive aggregated notes.
		// `allNotes` is a derived aggregation from:
		// - truck.notes
		// - driver1.notes / driver2.notes
		// - currentTrailer.notes
		// - currentLoad.notes (load is normalized in loadsById)
		//
		// Websocket patches often update nested driver/trailer objects without touching `patch.notes`,
		// so we must recompute `allNotes` on those updates too.
		const notesRelatedPatch =
			'notes' in patch ||
			'driver1' in patch ||
			'driver2' in patch ||
			'currentTrailer' in patch;
		if (notesRelatedPatch || tasksWereEdited) {
			const currentLoad = nextTruck.currentLoadId ? s.loadsById[nextTruck.currentLoadId] : null;
			nextTruck.allNotes = parseAndSortNotes({ ...nextTruck, currentLoad });
		}
		if (tasksWereEdited) {
			const wasLocked = lockedCode(prevTruck.tasks, statusNowMs) != null;
			const nowLocked = lockedCode(nextTruck.tasks, statusNowMs) != null;
			if (wasLocked || nowLocked) {
				nextTruck.manualBoardStatus = null;
			}
		}
		const nextTrucksAtHome = new Set(s.trucksAtHome);
		const wasAtHome = nextTrucksAtHome.has(truckId);
		if (truckIsAtHome !== null) {
			if (truckIsAtHome && !wasAtHome) {
				// Truck became at home
				nextTrucksAtHome.add(truckId);
			} else if (!truckIsAtHome && wasAtHome) {
				// Truck is no longer at home
				nextTrucksAtHome.delete(truckId);
			}
		}
		const nextOpenTrucks = new Set(s.openTrucks);
		const wasOpen = nextOpenTrucks.has(truckId);
		if (truckIsOpen !== null) {
			if (truckIsOpen && !wasOpen) {
				// Truck became open
				nextOpenTrucks.add(truckId);
			} else if (!truckIsOpen && wasOpen) {
				// Truck is no longer open
				nextOpenTrucks.delete(truckId);
			}
		}
		return {
			trucksById: {
				...s.trucksById,
				[truckId]: nextTruck,
			},
			trucksAtHome: nextTrucksAtHome,
			openTrucks: nextOpenTrucks,
			trucksTotalGross: nextTrucksTotalGross,
			trucksTotalDriverPayout: nextTrucksTotalDriverPayout,
			trucksWeeklyRpm: nextTrucksWeeklyRpm,
			trucksPayoutGross: nextTrucksPayoutGross,
			trucksPayoutRpm: nextTrucksPayoutRpm,
		};
	});

	// Ensure we have the load payloads needed for rendering:
	// - currentLoadId: load details should come from /loads/:id (full load page payload)
	// - upcomingLoadIdsForPlannerView: planner cards need /loads/loadForPlannerView/:id (must include stops, etc.)
	try {
		const state = get();
		const truck = state.trucksById?.[truckId];
		if (!truck) return;

		const { upsertLoad } = state;

		// 1) Current load: fetch full payload when missing or only planner-thin data exists
		const currentId = Number(truck.currentLoadId) || null;
		if (currentId) {
			const currentExisting = state.loadsById?.[currentId];
			if (!currentExisting?.full) {
				sendRequest(
					'GET',
					`${API_ROOT}loads/${currentId}`,
					null,
					(resp) => {
						try {
							upsertLoad(markFullLoad(resp));
						} catch (e) {}
					},
					() => {},
					null
				);
			}
		}

		// 2) Upcoming loads: fetch planner payload if missing OR not planner-ready
		const upcomingIds = (Array.isArray(truck.upcomingLoadIdsForPlannerView) ? truck.upcomingLoadIdsForPlannerView : [])
			.map((x) => Number(x) || null)
			.filter(Boolean);
		if (upcomingIds.length < 1) return;

		const uniqueUpcoming = Array.from(new Set(upcomingIds));
		const idsToFetchPlanner = [];
		for (const id of uniqueUpcoming) {
			if (currentId && id === currentId) continue;
			const existing = state.loadsById?.[id];
			if (existing?.full === true) continue;
			let shouldFetch = false;
			if (!existing) {
				shouldFetch = true;
			} else if (!Array.isArray(existing.stops)) {
				shouldFetch = true;
			}

			// If we temporarily have an "Unassigned" snapshot in store for a load that is now upcoming,
			// optimistically restore the previous status (if provided) to avoid UI flicker.
			if (existing && existing.loadStatus === "Unassigned") {
				const fallbackStatus = existing.lastLoadStatus;
				if (fallbackStatus && fallbackStatus !== existing.loadStatus) {
					try { upsertLoad({ id, loadStatus: fallbackStatus }); } catch (e) {}
				}
				// shouldFetch = true;
			}

			if (shouldFetch) idsToFetchPlanner.push(id);
		}

		for (const id of idsToFetchPlanner) {
			sendRequest(
				'GET',
				`${API_ROOT}loads/loadForPlannerView/${id}`,
				null,
				(resp) => {
					try { upsertLoad(resp); } catch (e) {}
				},
				() => {},
				null
			);
		}
	} catch (e) {}
	},

	upsertLoad: (load) => {
		if (!load || !load.id) return;
		// console.log(load.loadStatus)
		set((s) => ({
			loadsById: upsertLoadMap(s.loadsById, load),
		}));
	},

	// Invoice Page: ensure incoming live updates can add loads into the invoice list
	addInvoiceId: (id) => {
		const n = Number(id);
		if (!n) return;
		set((s) => {
			if (s.invoiceIds.includes(n)) return s;
			return { invoiceIds: [...s.invoiceIds, n] };
		});
	},
	upsertInvoiceLoad: (load) => {
		if (!load || !load.id) return;
		if (load.broker && !load.broker.factorInvoices)return;
		set((s) => {
			const id = Number(load.id);
			if (!id) return s;
			const nextIds = s.invoiceIds.includes(id) ? s.invoiceIds : [...s.invoiceIds, id];
			return {
			loadsById: upsertLoadMap(s.loadsById, load),
			invoiceIds: nextIds,
			};
		});
	},

	// For updates that change which loads are assigned to a truck (without full truck re-fetch)
	setTruckLoads: (truckId, { currentLoadId, upcomingIds, completedIds }) => {
		if (truckId == null) return;
		truckId = String(truckId);
		set((s) => {
			const truck = s.trucksById[truckId];
			if (!truck) return s;
			const next = { ...truck };
			if (currentLoadId !== undefined) next.currentLoadId = currentLoadId;
			if (upcomingIds !== undefined) next.upcomingLoadIdsForPlannerView = upcomingIds;
			if (completedIds !== undefined) next.previousLoadIdsForPlannerView = completedIds;
			
			// Ensure loads only appear in one category: remove from completed if they're now current/upcoming
			// Only do this when we're actually setting currentLoadId or upcomingIds (not when just setting completedIds)
			if (currentLoadId !== undefined || upcomingIds !== undefined) {
				const activeIds = new Set();
				if (next.currentLoadId) activeIds.add(Number(next.currentLoadId));
				if (Array.isArray(next.upcomingLoadIdsForPlannerView)) {
					next.upcomingLoadIdsForPlannerView.forEach(id => activeIds.add(Number(id)));
				}
				if (activeIds.size > 0 && Array.isArray(next.previousLoadIdsForPlannerView)) {
					next.previousLoadIdsForPlannerView = next.previousLoadIdsForPlannerView.filter(id => !activeIds.has(Number(id)));
				}
			}

			// Keep derived convenience fields in sync (without storing nested load objects).
			if (currentLoadId !== undefined) {
			const currentLoad = currentLoadId ? s.loadsById[currentLoadId] : null;
			next.currentLoadNumber = currentLoad?.loadNumber ?? null;
			next.allNotes = parseAndSortNotes({ ...next, currentLoad });
			}
			return {
			trucksById: {
				...s.trucksById,
				[truckId]: next,
			},
			};
		});
	},

	applyUnassignedLoadsUpdate: (data) => {
	if (!data) return;
	// We sometimes receive a partial load object here. If a currently opened (assigned) load becomes
	// unassigned, we want to reload it as if user clicked it in the Unassigned Loads table:
	// show loading -> GET /loads/:id -> upsertLoad -> openPopupLoad(id)
	const before = get();
	const sel = before.popupSelection;
	const openedLoadId = sel && sel.type === 'load' ? Number(sel.id) : null;
	const openedWasUnassigned =
		Boolean(openedLoadId) &&
		Array.isArray(before.unassignedLoads) &&
		before.unassignedLoads.some((l) => Number(l?.id) === openedLoadId);

	let shouldReloadOpenedAsUnassigned = false;
	let reloadId = null;

	// Decide reload based on payload type (snapshot vs delta)
	if (openedLoadId) {
		if (Array.isArray(data)) {
		const nowUnassigned = data.some((l) => Number(l?.id) === openedLoadId);
		shouldReloadOpenedAsUnassigned = !openedWasUnassigned && nowUnassigned;
		reloadId = shouldReloadOpenedAsUnassigned ? openedLoadId : null;
		} else if (data && typeof data === 'object' && 'load' in data) {
		const id = Number(data.load?.id);
		const add = Boolean(data.add);
		if (add && id && id === openedLoadId && !openedWasUnassigned) {
			shouldReloadOpenedAsUnassigned = true;
			reloadId = id;
		}
		}
	}

	set((s) => {
		// Supported payloads:
		// - Full snapshot: Load[]  -> replace list
		// - Delta: { add: boolean, load: Load } -> add/remove a single load
		if (Array.isArray(data)) {
		// Also upsert these loads into loadsById so we can open them
		let nextLoads = s.loadsById;
		for (const l of data) {
			nextLoads = upsertLoadMap(nextLoads, l);
		}
		// If currently opened popup is an unassigned load that disappeared, close it.
		const sel = s.popupSelection;
		if (sel && sel.type === 'load' && sel.id) {
			const openedId = Number(sel.id);
			const wasUnassigned = Array.isArray(s.unassignedLoads) && s.unassignedLoads.some((l) => Number(l?.id) === openedId);
			const stillUnassigned = data.some((l) => Number(l?.id) === openedId);
			if (wasUnassigned && !stillUnassigned) {
			return {
				unassignedLoads: data,
				loadsById: nextLoads,
				popupSelection: null,
			};
			}
		}
		return {
			unassignedLoads: data,
			loadsById: nextLoads,
		};
		}

		// Delta update: add/remove one load
		if (data && typeof data === 'object' && 'load' in data) {
		const load = data.load;
		const add = Boolean(data.add);
		if (!load || !load.id) return s;

		const current = Array.isArray(s.unassignedLoads) ? s.unassignedLoads : [];
		const id = Number(load.id);
		if (!id) return s;

		if (add) {
			// upsert in entity map
			const nextLoadsById = upsertLoadMap(s.loadsById, load);
			// avoid duplicates in list
			const exists = current.some((l) => Number(l?.id) === id);
			const nextList = exists ? current.map((l) => (Number(l?.id) === id ? { ...l, ...load } : l)) : [...current, load];
			return { unassignedLoads: nextList, loadsById: nextLoadsById };
		}

		// remove
		const existed = current.some((l) => Number(l?.id) === id);
		const nextList = current.filter((l) => Number(l?.id) !== id);
		// keep loadsById as-is (other parts of app may still reference this load)
		const sel = s.popupSelection;
		const shouldClosePopup = existed && sel && sel.type === 'load' && Number(sel.id) === id;
		return shouldClosePopup ? { unassignedLoads: nextList, popupSelection: null } : { unassignedLoads: nextList };
		}

		return s;
	});

	// Reload open popup after state update (avoid async work inside set()).
	if (shouldReloadOpenedAsUnassigned && reloadId) {
		try {
		// mimic UnassignedLoadsTable click behavior
		set({ popupSelection: { type: 'raw', obj: { loadNumber: true, loading: true } } });
		sendRequest(
			'GET',
			`${API_ROOT}loads/${reloadId}`,
			null,
			(resp) => {
			try {
				get().upsertLoad(resp);
			} catch (e) {}
			try {
				get().openPopupLoad(resp?.id || reloadId);
			} catch (e) {}
			},
			() => {},
			null
		);
		} catch (e) {}
	}
	},
	
	applyBoardNotesUpdate: (data) => {
	set((s) => {
		if (!data) return s;

		// If server ever sends a full array snapshot over websocket
		if (Array.isArray(data)) {
		return { boardNotes: sortBoardNotes(normalizeBoardNotes(data)) };
		}

		// Delta update: { note: {...}, removed: boolean }
		const incomingNote = data.note;
		const removed = Boolean(data.removed);
		if (!incomingNote || !incomingNote.id) return s;

		const current = Array.isArray(s.boardNotes) ? s.boardNotes : [];
		const id = incomingNote.id;

		if (removed) {
		return { boardNotes: sortBoardNotes(current.filter((n) => n?.id !== id)) };
		}

		const idx = current.findIndex((n) => n?.id === id);
		if (idx >= 0) {
		const next = [...current];
		next[idx] = { ...next[idx], ...incomingNote };
		return { boardNotes: sortBoardNotes(next) };
		}

		// Prepend new note so user sees it immediately
		return { boardNotes: sortBoardNotes([incomingNote, ...current]) };
	});
	},

	// Action to hydrate invoice data. Optional meta from loads/invoicesPage response.
	setInvoiceData: (loads, meta) => {
		if (loads == null && meta == null) return;
		set((s) => {
			let nextLoads = s.loadsById;
			const ids = [];
			if (Array.isArray(loads)) {
				for (const l of loads) {
					if (l.id) {
						nextLoads = upsertLoadMap(nextLoads, l);
						ids.push(l.id);
					}
				}
			}
			const uniqueIds = Array.from(new Set(ids.map((x) => Number(x)).filter(Boolean)));
			const patch = {
				loadsById: nextLoads,
				invoiceIds: uniqueIds,
			};
			if (meta != null && typeof meta === 'object') {
				patch.notFactoredManagableLoadExists = Boolean(meta.notFactoredInvoicedLoads);
				patch.completedButNotFactoredLoadsCount = Number(meta.notFactoredCompletedLoadCount) || 0;
			}
			return patch;
		});
	},

	setNotFactoredManagableLoadExists: (v) => set({ notFactoredManagableLoadExists: Boolean(v) }),
	setCompletedButNotFactoredLoadsCount: (n) => set({ completedButNotFactoredLoadsCount: Number(n) || 0 }),

}))
);
// useBoardStore.subscribe(
// 	state => [state.trucksById, state.loadsById],
// 	() => {
// 	  useBoardStore.getState().recomputeVisibleTrucks();
// 	}
// );
function matchesTruckData(truckNumber, truckData, searchText){
    try {
        if(truckNumber && truckNumber.includes(searchText)){
            return true;
        }
        if(truckData.driver1){
            const driverData = truckData.driver1;
            if(matches(`${driverData.firstName} ${driverData.middleName ? driverData.middleName + ' ':''}${driverData.lastName}`, searchText) || 
            matches(`${driverData.firstName} ${driverData.lastName}`, searchText) || 
            matches(driverData.phoneNumber, searchText, true)){
                return true;
            }
        }
        if(truckData.driver2){
            const driverData = truckData.driver2;
            if(matches(`${driverData.firstName} ${driverData.middleName ? driverData.middleName + ' ':''}${driverData.lastName}`, searchText) || 
            matches(`${driverData.firstName} ${driverData.lastName}`, searchText) || 
            matches(driverData.phoneNumber, searchText, true)){
                return true;
            }
        }
        if(truckData.currentTrailerNumber && truckData.currentTrailerNumber.toLowerCase().includes(searchText)){
            return true;
        }else{
            
        }
        if(truckData.currentLoad){
            if(matches(truckData.currentLoad.loadNumber, searchText) || matches(truckData.currentLoad.broker, searchText)){
                return true;
            }
        }
		// Check upcoming loads
        if(truckData.upcomingLoads && Array.isArray(truckData.upcomingLoads)){
            for(const upcomingLoad of truckData.upcomingLoads){
                if(matches(upcomingLoad.loadNumber, searchText) || matches(upcomingLoad.broker, searchText)){
                    return true;
                }
            }
        }
    } catch (error) {}
    return false;
}
function matches(optionalVal, searchText, removeSpacesAndSpecialChars){
    if(optionalVal){
        optionalVal = optionalVal.toLowerCase();
        if(removeSpacesAndSpecialChars){
            return optionalVal.replace(/[^a-zA-Z0-9]/g, "").includes(searchText.replace(/[^a-zA-Z0-9]/g, ""))
        }else{
            return optionalVal.includes(searchText);
        }
    }
    return false; 
}
function calculateTruckGross(thisWeeksLoads) {
	if (!thisWeeksLoads || !Array.isArray(thisWeeksLoads)) return 0;
	let totalGross = 0;
	for (const load of thisWeeksLoads) {
		if (load) {
			const loadData = typeof load === 'string' ? JSON.parse(load) : load;
			if (!loadHasBoardShare(loadData)) continue;
			if (loadData && loadData.amountForInvoice != null && loadData.amountForInvoice !== '') {
				totalGross += Number(loadData.amountForInvoice) || 0;
			}
		}
	}
	return totalGross;
}

function assignTruckRateTotals(truckId, truck, buckets) {
	const followsWeekly = followsWeeklyOwnerPayoutFromCompany(retrieveCompanyData());
	const loads = truck?.thisWeeksLoads;
	const payoutLoads = truck?.thisWeeksDriverPayoutLoads;
	buckets.trucksTotalGross[truckId] = calculateTruckGross(loads);
	buckets.trucksTotalDriverPayout[truckId] = calculateTruckDriverPayoutTotal(payoutLoads);
	buckets.trucksWeeklyRpm[truckId] = calculateTruckWeeklyRpm(loads);
	if (followsWeekly) {
		buckets.trucksPayoutGross[truckId] = calculateJoinedPayoutGross(loads, payoutLoads);
		buckets.trucksPayoutRpm[truckId] = calculateTruckPayoutRpm(loads, payoutLoads);
	} else {
		buckets.trucksPayoutGross[truckId] = buckets.trucksTotalDriverPayout[truckId];
		buckets.trucksPayoutRpm[truckId] = null;
	}
}

/** Defensive copy with amountForInvoice cleared (WS patches / hydrate). */
function sanitizeThisWeeksLoadsAmounts(thisWeeksLoads) {
	if (!thisWeeksLoads || !Array.isArray(thisWeeksLoads)) return thisWeeksLoads;
	return thisWeeksLoads.map((load) => {
		if (load == null) return load;
		const loadData = typeof load === 'string' ? JSON.parse(load) : load;
		if (!loadData || typeof loadData !== 'object') return load;
		return { ...loadData, amountForInvoice: null };
	});
}

/** null grossVisibleTruckIds = unrestricted. */
export function isTruckGrossVisibleOnBoard(truckId) {
	const set = useBoardStore.getState().grossVisibleTruckIds;
	if (set == null) return true;
	if (truckId == null) return false;
	return set.has(String(truckId));
}

/** Full truck page: backend nulls amounts when restricted for this truck. */
export function thisWeeksLoadsHaveVisibleRates(loads) {
	if (!loads || loads.length === 0) return true;
	return loads.some((l) => {
		const load = typeof l === 'string' ? JSON.parse(l) : l;
		return load && load.amountForInvoice != null && load.amountForInvoice !== '';
	});
}